import * as React from "react";
import * as ReactDOM from "react-dom/client";
import { initializeIcons } from "office-ui-fabric-react/lib/Icons";
import App from "./components/App";
import "./styles.less";
import "./global.css";
import "office-ui-fabric-react/dist/css/fabric.min.css";
import { Provider } from "react-redux";
import store from "./components/Redux/store";
import { Auth0Provider } from "@auth0/auth0-react";
import { API_BASE_URL, domain, audience, scope, clientId, spaClientId } from "./config/config";
import { SaveCommuncation } from "./components/Redux/communicationSlice";
import { LOCALSTORAGE_KEYS } from "./utils/Constants";
// OnMessageSend / attachment-handling business logic. Previously loaded via
// a standalone <script src="legacy/messageSendHandlers.js"> tag in
// index.html - that was one extra sequential network request before Outlook
// could find window.onMessageSendHandler, which was fast enough over
// localhost but not from the hosted server, causing Outlook Classic's send
// handler to time out. Importing it here bundles it into this same
// app.[hash].js instead, removing that extra request. It attaches
// window.onMessageSendHandler / window.CallfromPopup itself at the bottom of
// the file, since as a bundled module its top-level function declarations
// are no longer automatically global the way a classic <script> tag's were.
import "./legacy/messageSendHandlers.js";

initializeIcons();

/**
 * One-time cleanup of raw tokens that older builds left sitting in
 * localStorage under our OWN keys (AccessToken / authToken / authToken1 -
 * see tokenManager.ts, which never reads these anymore). Existing users'
 * browsers already have these from before the token-security migration;
 * nothing removes them automatically otherwise, since browsers don't
 * expire localStorage on their own. Safe to run unconditionally.
 *
 * NOTE: this deliberately does NOT touch the @@auth0spajs@@::... keys the
 * Auth0 SDK itself writes - cacheLocation="localstorage" below means those
 * ARE the mechanism the browser/modern-host login path uses to survive the
 * add-in being closed and reopened. Deleting them here would defeat that
 * on every single reload.
 */
function purgeLegacyTokenStorage() {
    try {
        localStorage.removeItem(LOCALSTORAGE_KEYS.AccessToken);
        localStorage.removeItem(LOCALSTORAGE_KEYS.AuthToken);
        localStorage.removeItem(LOCALSTORAGE_KEYS.AuthToken1);
    } catch {
        // localStorage unavailable - nothing to clean up
    }
}
purgeLegacyTokenStorage();

let isOfficeInitialized = false;
window.dispatch = store.dispatch;
const title = "Office-Add-in-Microsoft-Graph-React";

// Get the root element for rendering
const container = document.getElementById("container");
const root = ReactDOM.createRoot(container!);

// Function to render the app
const render = (Component: React.ElementType) => {
    root.render(
        // <React.StrictMode>
            <Auth0Provider
                domain={domain}
                clientId={spaClientId}
                authorizationParams={{
                    // offline_access is required so Auth0 actually issues a
                    // refresh token for useRefreshTokens below - without it,
                    // the SDK would silently fall back to hidden-iframe
                    // silent auth, which is what we're trying to avoid.
                    scope: "openid profile email offline_access",
                    audience: audience,
                    redirect_uri: window.location.origin,
                }}
                // cacheLocation="localstorage": deliberate trade-off, chosen
                // after cacheLocation="memory" turned out to log
                // browser/modern-host users out every time the add-in was
                // closed and reopened (that path relies on loginWithPopup,
                // which we're keeping as-is, and a memory-only cache has
                // nothing left to resume from after a reload - the fallback,
                // a hidden-iframe silent-auth against Auth0's session
                // cookie, needs third-party cookies and a custom Auth0
                // domain to work reliably, neither of which is available
                // here). This means the SDK's own @@auth0spajs@@::... cache
                // (id_token/access_token, and the refresh token since
                // useRefreshTokens is on) sits in localStorage again for
                // this login path - the exact thing removed earlier in this
                // migration. Two things limit the blast radius of that:
                // Refresh Token Rotation is enabled on the Auth0 side (each
                // use invalidates the previous refresh token), and the
                // classic-Outlook login path is entirely unaffected - it
                // still never touches localStorage at all (session handle
                // in sessionStorage only, refresh brokered through the
                // Public API - see tokenManager.ts).
                cacheLocation="localstorage"
                useRefreshTokens={true}
                // Safety net for exactly the failure this add-in hit: when Auth0
                // declines to issue a refresh token to the browser-side code
                // exchange, auth0-spa-js v2 defaults this to FALSE and so
                // getAccessTokenSilently() throws `missing_refresh_token` instead
                // of falling back to hidden-iframe silent auth - the add-in then
                // looks logged out and re-prompts for credentials. Turning it on
                // degrades gracefully instead of failing hard. It is a fallback,
                // not the fix: iframe silent auth needs third-party cookies, which
                // WebView2/Edge may block, so the Auth0-side app config
                // (SPA type + Refresh Token Rotation) still has to be correct.
                useRefreshTokensFallback={true}
            >
                <Provider store={store}>
                    <Component title={title} isOfficeInitialized={isOfficeInitialized} />
                </Provider>
            </Auth0Provider>
        // </React.StrictMode>
    );
};

// Function to handle item change in Outlook
function onItemChanged(eventArgs: Office.AddinCommands.Event) {
    const item = Office.context.mailbox.item;
    if (item) {
        render(App);
    }
}


/* Initial render showing a progress bar */
render(App);

if ((module as any).hot) {
    (module as any).hot.accept("./components/App", () => {
        const NextApp = require("./components/App").default;
        render(NextApp);
    });
}
